import { useMemo } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle";
import { resources } from "@constants/hardcoded-strings";
import { WorkflowReportRpServiceConfiguration, WorkflowReportStepByStepResponse } from "@idw-types/data/reports";
import { WorkflowReportSearchFiltersState } from "@idw-types/state/workflow-report-state";
import { CardContainer } from "../../../common/card-container/card-container";
import { ContentPageBody } from "../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../common/content-page-header/content-page-header";
import { WorkflowReportStepByStepChart } from "./workflow-report-step-by-step-chart/workflow-report-step-by-step-chart";
import styles from "./workflow-report-step-by-step-container.less";
import { WorkflowLegendContainer } from "../workflow-legend-container/workflow-legend-container";
import { WORKFLOW_STEP_BY_STEP_LEGEND_ITEMS } from "@constants/workflow-report";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";

type WorkflowReportStepByStepContainerProps = {
    stepByStepResponse: WorkflowReportStepByStepResponse;
    rpServices: Array<WorkflowReportRpServiceConfiguration>;
    searchFilters: WorkflowReportSearchFiltersState;
    isLoading: boolean;
};

export const WorkflowReportStepByStepContainer = (props: WorkflowReportStepByStepContainerProps) => {
    const { stepByStepResponse, rpServices, isLoading } = props;

    const stepByStepTooltip = useMemo(() => {
        const renderTooltip = (props: any) => {
            return <Tooltip {...props}>{resources.pages.workflowReport.sections.stepByStep.tooltip}</Tooltip>
        }
        return (
            <OverlayTrigger overlay={renderTooltip} placement={"auto"}>
                <div><FontAwesomeIcon icon={faQuestionCircle} /></div>
            </OverlayTrigger>
        );
    }, []);

    const counts = useMemo(() => {
        if (stepByStepResponse !== null) {
            return {
                stepCount: stepByStepResponse.length,
                completeApprovedCount: stepByStepResponse.map(step => step.completeApproved).reduce((a, b) => a + b, 0),
                completeDeniedCount: stepByStepResponse.map(step => step.completeDenied).reduce((a, b) => a + b, 0),
                proceedPassCount: stepByStepResponse.map(step => step.proceedPass).reduce((a, b) => a + b, 0),
                proceedFailCount: stepByStepResponse.map(step => step.proceedFail).reduce((a, b) => a + b, 0),
                abandonedCount: stepByStepResponse.map(step => step.abandoned).reduce((a, b) => a + b, 0)
            };
        }

        return {};

    }, [stepByStepResponse]);

    const description = useMemo(() => {
        if (stepByStepResponse !== null) {
            return resources.pages.workflowReport.sections.stepByStep.description(
                counts.stepCount,
                counts.completeApprovedCount,
                counts.completeDeniedCount,
                counts.proceedPassCount,
                counts.proceedFailCount,
                counts.abandonedCount
            );
        }

        return "";

    }, [counts, stepByStepResponse]);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.workflowReport.sections.stepByStep.title} headerButton={stepByStepTooltip} />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && <LoadingIndicator title={resources.common.loadingIndicatorText} />}
                {(!isLoading && stepByStepResponse && rpServices) &&
                    <>
                        <div className={styles.workflowReportDescription}>
                            <span id="workflow-report-step-by-step-description" dangerouslySetInnerHTML={{ __html: description }} />
                            <WorkflowLegendContainer
                                items={WORKFLOW_STEP_BY_STEP_LEGEND_ITEMS}
                            />
                        </div>

                        {counts.stepCount > 0 && (
                            <div className={styles.graphContainer}>
                                <div className={styles.workflowReportStepByStepChartContainer}>
                                    <WorkflowReportStepByStepChart
                                        stepByStepResponse={stepByStepResponse}
                                        rpServices={rpServices}
                                    />
                                </div>


                            </div>
                        )}
                    </>
                }

            </ContentPageBody>
        </CardContainer>
    );
};